<template>
    <div class="Container">
        <FadeTransition mode="out-in">
            <WLoading
                v-if="loading"
                visible
            />
        </FadeTransition>
        <div
            v-show="!loading"
            class="Wrapper"
        >
            <div class="Top">
                <slot
                    name="top"
                    v-bind="{ grid: gridApi }"
                />
                <WButton
                    v-if="cleanFiltersButton"
                    @click="cleanFilters"
                >
                    {{ $t('actions.cleanFilters') }}
                </WButton>
            </div>
            <AgGridVue
                :key="agGridKey"
                class="DataGrid"
                :class="[theme]"
                :columnDefs="actualColumnDefs"
                :rowData="transformedRows"
                :gridOptions="gridOptions"
                :filterable="filterable"
                :hasInputFilter="hasInputFilter"
                :paginable="paginable"
                :resizable="resizable"
                :selectable="selectable"
                :frameworkComponents="frameworkComponents"
                :suppressCellSelection="hideCellSelection"
                :context="context"
                :modules="gridModules"
                :components="components"
                :statusBar="statusBar"
                v-bind="$attrs"
                v-on="gridListeners"
            />
            <div class="Bottom">
                <slot
                    name="bottom"
                    v-bind="{ grid: gridApi }"
                />
            </div>
        </div>
    </div>
</template>

<script>
/* eslint-disable no-unreachable */
import VueTypes from 'vue-types'
import { merge } from 'lodash'
import { AgGridVue } from 'ag-grid-vue'
import { ClipboardModule } from '@ag-grid-enterprise/clipboard'
import Config from '@/config'
import { TYPES } from '@/entities/fields'
import { getColumnDefs } from './utils'
import FrameworkComponents from './components'

export default {
    name: 'DataGrid',
    components: {
        AgGridVue,
    },
    props: {
        // You can use 'columnDefs' directly or 'columns' and 'selectGroups' that they will compute that 'columnDefs' value internally.
        columnDefs: VueTypes.arrayOf(VueTypes.object).optional,
        columns: VueTypes.arrayOf(VueTypes.object).optional,
        selectGroups: VueTypes.object.def({}),
        rows: VueTypes.array.def([]),
        loading: VueTypes.bool.def(false),
        sideBar: VueTypes.bool.def(true),
        filterable: VueTypes.bool.def(true),
        hasInputFilter: VueTypes.bool.def(true),
        paginable: VueTypes.bool.def(false),
        selectable: VueTypes.oneOf(['single', 'multiple']).def('multiple'),
        resizable: VueTypes.bool.def(true),
        sortable: VueTypes.bool.def(true),
        editable: VueTypes.bool.def(true),
        fitResponsive: VueTypes.bool.def(false),
        cleanFiltersButton: VueTypes.bool.def(false),
        hideCellSelection: VueTypes.bool.def(false),
        enableCellTextSelection: VueTypes.bool.def(true),
        headerHeight: VueTypes.number.def(48),
        extraColumns: VueTypes.any,
        defaultColDef: VueTypes.any,
        theme: VueTypes.oneOf(['ag-theme-material', 'ag-theme-alpine', 'ag-theme-alpine-dark', 'ag-theme-balham', 'ag-theme-balham-dark']).def(Config.theme.grid),
        module: VueTypes.array.def([]),
        contextMenu: VueTypes.oneOfType([VueTypes.func, VueTypes.array]),
        tooltipShowDelay: VueTypes.number.def(0),
        statusBar: VueTypes.shape({
            statusPanels: VueTypes.any,
        }).loose,
        actions: VueTypes.arrayOf(VueTypes.shape({
            icon: VueTypes.string,
            action: VueTypes.func,
        })),
        components: VueTypes.any,
        formatDate: VueTypes.string.def('DD/MM/YYYY'),
    },
    data () {
        return {
            agGridKey: 1, // AgGrid is not reactive to gridOptions prop, so we will change this key to force an update
            gridApi: null,
            context: undefined,
        }
    },
    computed: {
        actualColumnDefs () {
            const {
                columnDefs, columns, selectGroups, formatDate,
            } = this
            return columnDefs || getColumnDefs({ columns, selectGroups, formatDate })
        },
        transformedRows () {
            return this.rows.map(row => ({
                ...row,
            }))
        },
        gridModules () {
            return [
                ClipboardModule,
                ...this.module,
            ]
        },
        gridOptions () {
            const filterOpts = {
                filter: true,
                filterParams: {
                    suppressAndOrCondition: false,
                    resetButton: true,
                    clearButton: true,
                },
            }
            const sideBarOpts = {
                toolPanels: [
                    {
                        id: 'columns',
                        labelDefault: 'Columns',
                        labelKey: 'columns',
                        iconKey: 'columns',
                        toolPanel: 'agColumnsToolPanel',
                        toolPanelParams: {
                            suppressPivotMode: true,
                            suppressRowGroups: true,
                            suppressValues: true,
                        },
                    },
                ],
                position: 'left',
            }
            if (this.filterable) {
                sideBarOpts.toolPanels.push('filters')
            }

            return {
                defaultColDef: {
                    ...(this.defaultColDef ? this.defaultColDef : {}),
                    ...(this.filterable ? filterOpts : { filter: false }),
                    resizable: this.resizable,
                    sortable: this.sortable,
                    editable: this.editable,
                    menuTabs: ['filterMenuTab', 'columnsMenuTab', 'generalMenuTab'],
                    ...(this.fitResponsive ? { flex: 1 } : {}),
                },
                columnTypes: {
                    ...Object.values(TYPES).reduce((acc, type) => ({ ...acc, [type]: {} }), {}),
                },
                suppressPropertyNamesCheck: true, // Disables showing a warning message in the console if using a gridOptions or colDef property that doesn't exist.
                headerHeight: this.headerHeight,
                floatingFilter: this.hasInputFilter,
                animateRows: true,
                pagination: this.paginable,
                paginationAutoPageSize: this.paginable, // No scroll in the table, adds more pages
                suppressMenuHide: true, // To show always the menu
                rowSelection: this.selectable,
                suppressRowClickSelection: !this.selectable,
                sideBar: this.sideBar ? sideBarOpts : [],
                enableCellTextSelection: this.enableCellTextSelection,
                getContextMenuItems: this.getContextMenuItems,
                tooltipShowDelay: this.tooltipShowDelay,
            }
        },
        gridListeners () {
            return merge({}, this.$listeners, {
                'grid-ready': this.handleGridReady,
                cellEditingStarted: data => this.$emit('onCellEditingStarted', data),
                cellEditingStopped: data => this.$emit('onCellEditingStopped', data),
                cellValueChanged: data => this.$emit('onCellChanged', data),
                'cell-clicked': data => this.$emit('onCellClick', data),
                cellDoubleClicked: data => this.$emit('onCellDoubleClicked', data),
                cellMouseOver: data => this.$emit('onCellMouseOver', data),
                cellMouseOut: data => this.$emit('onCellMouseOut', data),
                'row-clicked': data => this.$emit('onRowClick', data),
                rowDoubleClicked: data => this.$emit('onRowDoubleClicked', data),
                filterChanged: data => this.$emit('onFilterChanged', data),
                rowSelected: data => this.$emit('onRowSelected', data),
                selectionChanged: data => this.$emit('onSelectionChanged', data),
                displayedColumnsChanged: data => this.$emit('onDisplayColumnsChanged', data),
                rowDragEnter: data => this.$emit('onRowDragEnter', data),
                rowDragMove: data => this.$emit('onRowDragMove', data),
                rowDragEnd: data => this.$emit('onRowDragEnd', data),
            })
        },
    },
    watch: {
        gridOptions (newValue) {
            // AgGrid is not reactive to gridOptions prop, so we will change this key to force an update
            this.agGridKey = Math.round(Math.random() * 1e5)
        },
    },
    beforeCreate () {
        this.frameworkComponents = FrameworkComponents
    },
    beforeMount () {
        this.context = { componentParent: this }
    },
    methods: {
        cleanFilters () {
            this.gridApi.setFilterModel(null)
        },
        handleGridReady (params) {
            this.gridApi = params.api
            this.$emit('onGridReady', params)
            if (this.fitResponsive) {
                this.gridApi.sizeColumnsToFit()
                window.onresize = () => {
                    this.gridApi.sizeColumnsToFit()
                }
            }
        },
        getFilters () {
            return this.gridApi.getFilterModel()
        },
        setFilters (filters) {
            return this.gridApi.setFilterModel(filters)
        },
        removeRow (rowSelected) {
            this.gridApi.updateRowData({ remove: [rowSelected.data] })
        },
        getContextMenuItems (params) {
            // Doc: https://www.ag-grid.com/javascript-grid-context-menu/
            const renderIcon = icon => `<i class="WIcon icon-${icon} ag-icon" />`
            if (typeof this.contextMenu === 'function') return this.contextMenu(params)
            if (Array.isArray(this.contextMenu)) return this.contextMenu
            return [
                'paste',
                'copy',
                'separator',
                {
                    name: this.$t('entities.actions.export'),
                    action: () => params.api.exportDataAsCsv({ columnSeparator: '\t' }),
                    icon: renderIcon('download'),
                },
            ]
        },
    },
}
</script>

<style scoped lang="scss">
.Container {
    position: relative;
    display: flex;
	justify-content: space-between;
	align-items: stretch;
}

.Wrapper {
    display: flex;
    flex: 1;
    flex-direction: column;
}

.DataGrid {
    height: 100%;
    width: 100%;
}
</style>
